import {
  Box,
  Typography,
  Paper,
  Button
} from "@mui/material";
import {
  StickyNote2 as NotesIcon,
  Add as AddIcon
} from "@mui/icons-material";
import ClientNote from "./ClientNote";
import {useEffect, useState} from "react";
import NoteDialog from "./NoteDialog";
import {getClientNotes, saveClientNote} from "../../services/TraineeService";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {useMessageContext} from "../../contexts/MessageContext";
import {colors, borders, shadows} from "../ui/colors";
import {t as coachT} from "../dashboard/coach/coachTokens";
import {RTL} from "../../cache/CacheRtl";

const ClientPageNotes = ({client, coach = false, onNotesChanged}) => {
  const [newNote, setNewNote] = useState(false);
  const [clientNotes, setClientNotes] = useState([]);
  const {notificationHandler} = useMessageContext();
  const [reload, setReload] = useState(false);

  useEffect(() => {
    if (!client?.id) return;
    getClientNotes(client.id).then(r => {
      const notes = r.data || [];
      setClientNotes(notes);
      onNotesChanged?.(notes.length > 0);
    }).catch(e => {
      notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, client?.id]);

  const handleNewNote = (note) => {
    const body = {
      traineeId: client.id,
      message: note.message,
      // already uploaded by the dialog — what travels here is the CDN url and the name the
      // trainer knows the file by, since the url's own basename is generated
      fileUrl: note.fileUrl,
      fileName: note.fileName
    }
    saveClientNote(body)
        .then(() => setNewNote(false))
        .then(() => setReload(prv => !prv))
        .then(() => notificationHandler.success("פתק נשמר בהצלחה"))
        .catch(e => {
          notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
        })
  }

  return (
      <>
        <NoteDialog
            initValue={{}}
            approve={handleNewNote}
            close={() => setNewNote(false)}
            isOpen={newNote}
            coach={coach}
        />

        <Box sx={coach ? {} : { margin: "20px" }}>
          <RTL>
          <Paper
              dir="rtl"
              sx={{
                backgroundColor: coach ? coachT.white : colors.white,
                border: coach ? `1px solid ${coachT.border}` : borders.default,
                borderRadius: coach ? coachT.r : borders.radius.lg,
                boxShadow: coach ? coachT.sh : shadows.card,
                overflow: "hidden",
              }}
              elevation={0}
          >
            {/* Header */}
            <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: coach ? "16px 22px" : "16px 20px",
                  backgroundColor: coach ? coachT.bg : colors.bg,
                  borderBottom: coach ? `1px solid ${coachT.border}` : borders.default
                }}
            >
              <Box sx={{display: "flex", alignItems: "center", gap: coach ? 1.5 : 1}}>
                {coach ? (
                    <Box sx={{width: 40, height: 40, borderRadius: "11px", background: coachT.white, border: `1px solid ${coachT.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                      <NotesIcon sx={{color: coachT.t2, fontSize: 22}}/>
                    </Box>
                ) : (
                    <NotesIcon sx={{ color: colors.textSecondary, fontSize: "20px" }} />
                )}
                <Typography
                    sx={{
                      fontWeight: coach ? 800 : 600,
                      fontSize: coach ? "17px" : "16px",
                      color: coach ? coachT.text : colors.textPrimary,
                      letterSpacing: coach ? "-0.2px" : 0,
                    }}
                >
                  הערות לקוח
                </Typography>
              </Box>

              {coach ? (
                  <Box component="button" onClick={() => setNewNote(true)} sx={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    background: coachT.green, color: "#fff",
                    height: 34, px: "15px", borderRadius: coachT.rs,
                    fontSize: "13px", fontWeight: 600,
                    border: "none", cursor: "pointer", fontFamily: "inherit",
                    transition: "all .14s",
                    "&:hover": {background: coachT.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                  }}>
                    <AddIcon sx={{fontSize: 16}}/>
                    הוסף הערה
                  </Box>
              ) : (
                  <Button
                      onClick={() => setNewNote(true)}
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{
                        backgroundColor: colors.success,
                        color: colors.white,
                        fontSize: "13px",
                        padding: "6px 16px",
                        "&:hover": {
                          backgroundColor: colors.successText
                        }
                      }}
                      size="small"
                  >
                    הוסף הערה
                  </Button>
              )}
            </Box>

            {/* Content */}
            <Box sx={{ padding: coach ? "16px 22px" : "20px" }}>
              {clientNotes.length > 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: coach ? 1.25 : 2 }}>
                    {clientNotes
                        .sort((a, b) => b.createdDate - a.createdDate)
                        .map((note, i) => (
                            <ClientNote
                                reload={() => setReload(prv => !prv)}
                                key={i}
                                note={note}
                                coach={coach}
                            />
                        ))
                    }
                  </Box>
              ) : (
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "200px",
                    flexDirection: "column",
                    gap: 2
                  }}>
                    <NotesIcon sx={{ fontSize: "48px", color: coach ? coachT.border : colors.borderHover }} />
                    <Typography sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: coach ? coachT.t3 : colors.textSecondary,
                      textAlign: "center"
                    }}>
                      אין הערות עדיין
                    </Typography>
                    <Typography sx={{
                      fontSize: "14px",
                      color: coach ? coachT.t3 : colors.textMuted,
                      textAlign: "center"
                    }}>
                      לחץ על "הוסף הערה" כדי להוסיף הערה ראשונה
                    </Typography>
                  </Box>
              )}
            </Box>
          </Paper>
          </RTL>
        </Box>
      </>
  );
};


export default ClientPageNotes;